import type { Part } from 'src/CrudManager/Core/types/models'
import { axiosApi } from 'src/CrudManager/Core/Utils/axios'
import { ref } from 'vue'

// Interface for the raw data structure from the backend for a single item's stock info
interface RawPairedStockItemData {
  item_id: string
  item_name: string
  part_details: Part
  source_warehouse_stock: {
    warehouse_id: string
    warehouse_name: string
    quantity: number
  }
  destination_warehouse_stock: {
    warehouse_id: string
    warehouse_name: string
    quantity: number
  }
}

export interface PairedStockItem extends RawPairedStockItemData {
  // For UI interaction, not from backend directly for this item
  transfer_to_destination_quantity: number | null
  transfer_to_source_quantity: number | null
}

export interface StockTransferPayload {
  item_id: string
  quantity: number
  source_warehouse_id: string
  destination_warehouse_id: string
  part_option_id?: string | null
}

export function useStockTransfer() {
  const loading = ref(false)
  const error = ref<string | null>(null)

  async function getPairedStock(sourceWarehouseId: string, destinationWarehouseId: string, itemIds: string[]): Promise<PairedStockItem[]> {
    if (!itemIds.length) return []
    try {
      loading.value = true
      error.value = null
      const response = await axiosApi.post<{ data: RawPairedStockItemData[] }>('/stock/paired-stock', {
        source_warehouse_id: sourceWarehouseId,
        destination_warehouse_id: destinationWarehouseId,
        item_ids: itemIds,
      })
      // Initialize transfer quantities for UI
      return response.data.data.map(
        (item: RawPairedStockItemData): PairedStockItem => ({
          ...item,
          transfer_to_destination_quantity: null,
          transfer_to_source_quantity: null,
        }),
      )
    } catch (err) {
      console.error('Failed to fetch paired stock:', err)
      error.value = err instanceof Error ? err.message : 'Failed to fetch paired stock'
      throw err
    } finally {
      loading.value = false
    }
  }

  async function processTransfers(transfers: StockTransferPayload[], bypassQuantityLimits: boolean) {
    try {
      loading.value = true
      error.value = null

      await axiosApi.post('/stock/transfer', {
        transfers,
        bypass_quantity_limits: bypassQuantityLimits,
      })

      return true
    } catch (err) {
      console.error('Failed to process transfers:', err)
      error.value = err instanceof Error ? err.message : 'Failed to process transfers'
      throw err
    } finally {
      loading.value = false
    }
  }

  return {
    loading,
    error,
    getPairedStock,
    processTransfers,
  }
}
